import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useRef, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { ToggleButton } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

/** Same keys as `borrowerEmailOptions` on the backend. Used if the API has not sent the list yet. */
const fallbackBorrowerEmails: Record<string, string> = {
  borrowerCreation: 'Borrower Account Creation',
  documentSignRequest: 'Document Sign Request',
  sendAssetVerification: 'Assets Verification',
  paymentPullCreditScoreReport: 'Payment for Pull Credit Score Report',
  appraisalDrawStatusNotification: 'Draw Status Notification',
  postClosingUploadPdf: 'Post Closing PDF',
  transferLetterToBorrower: 'Transfer Letter to the Borrower',
  servicingTransferToBorrower: 'Notice of Servicing Transfer to the Borrower',
  loanActionToBorrower: 'Loan Action Emails to the Borrower',
}

const borrowerEmailHints: Record<string, string> = {
  transferLetterToBorrower: 'Sends to the borrower only when the Transfer Letter email has no recipient of its own.',
  servicingTransferToBorrower:
    'Sends to the borrower only when the Servicing Transfer email has no recipient of its own.',
  loanActionToBorrower: 'Loan actions and tasks that are addressed to the borrower.',
}

const storedConfig = (data: Record<string, any>) => {
  const next = cloneDeep(data)
  delete next.borrowerEmailList
  return next
}

export const EmailPartiesConfig = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, any>>({})
  const dataRef = useRef<Record<string, any>>({})

  useEffect(() => {
    setLoading(true)
    getAdminConfig('emailParties').then((res) => {
      dataRef.current = res
      setData(res)
      setLoading(false)
    })
  }, [])

  const titles: Record<string, string> = data.borrowerEmailList || fallbackBorrowerEmails
  const masterOn = !!data.emailEnabled?.borrower

  const isEmailOn = (key: string) => {
    const individual = data.borrowerEmails?.[key]
    if (typeof individual === 'boolean') return individual
    return masterOn
  }

  const persist = async (temp: Record<string, any>) => {
    dataRef.current = temp
    setData(temp)
    setLoading(true)
    await setAdminConfig('emailParties', storedConfig(temp))
    setLoading(false)
  }

  const onChangeAll = async (_key: string, value: boolean) => {
    const temp = cloneDeep(data)
    if (!temp.emailEnabled) temp.emailEnabled = {}
    temp.emailEnabled.borrower = value
    temp.borrowerEmails = {}
    Object.keys(titles).forEach((key) => {
      temp.borrowerEmails[key] = value
    })
    await persist(temp)
  }

  const onChangeOne = async (key: string, value: boolean) => {
    const temp = cloneDeep(data)
    if (!temp.borrowerEmails) temp.borrowerEmails = {}
    // Materialize the rest from the all-switch so one change does not flip the others.
    Object.keys(titles).forEach((emailKey) => {
      if (typeof temp.borrowerEmails[emailKey] !== 'boolean') temp.borrowerEmails[emailKey] = masterOn
    })
    temp.borrowerEmails[key] = value
    await persist(temp)
  }

  return (
    <div className="flex flex-col gap-4">
      <LayoutLoading show={isLoading} />
      <RenderInput
        input={{
          inputType: 'togglebutton',
          title: 'Enable / Disable All Borrower Email',
          value: masterOn,
        }}
        Key="emailEnabled"
        onChange={onChangeAll}
      />
      <p className="text-sm text-gray-500 -mt-2">
        This sets every email below. Each one can then be turned on or off on its own. An email left unset follows
        this switch.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {Object.keys(titles).map((key, index) => (
          <div key={key} className="mb-2">
            <ToggleButton
              id={key}
              title={`${index + 1}. ${titles[key]}`}
              tooltip={borrowerEmailHints[key] || ''}
              value={isEmailOn(key)}
              onChange={(value) => onChangeOne(key, value)}
              textLeft
            />
          </div>
        ))}
      </div>
    </div>
  )
}
